import type { ConversationBoardCard } from '@voll-intelligence/domain';
import { useTranslation } from 'react-i18next';

import { Avatar, AvatarFallback, Card } from '@web/ui/core/atoms';
import { ChannelChip, ProgressBar, RoleBadge, ToneChip, VipBadge } from '@web/ui/core/molecules';

export interface ConversationCardProps {
  card: ConversationBoardCard;
  selected?: boolean;
  onClick?: () => void;
}

/**
 * Conversation card — one row per card on the Conversas board.
 *
 * Footer task rollup (progress pips + done/total) comes from
 * `card.taskProgress`, derived server-side by the conversation board
 * aggregation. The mockup's actor chip needs an agent display name not
 * carried on the board card yet — omitted until the read-model exposes it.
 */
export function ConversationCard({
  card,
  selected,
  onClick,
}: Readonly<ConversationCardProps>): React.JSX.Element {
  const { t } = useTranslation();
  const { total, done, statuses } = card.taskProgress;
  const waiting = statuses.includes('blocked') && !statuses.includes('running');
  const actor =
    card.assigneeAgent && card.agentDisplayName
      ? { kind: card.assigneeAgent.kind, name: card.agentDisplayName }
      : null;
  const showFooter = total > 0 || actor !== null;
  return (
    <Card
      active={selected}
      onTap={onClick}
      aria-label={card.subject ?? t('board.card.fallbackAria', { sequence: card.seq })}
    >
      <div
        data-testid="conversation-card"
        data-card-id={card.id}
        className="flex min-h-card flex-col gap-2 p-3"
      >
        <div className="flex items-center gap-1.5 font-mono text-card-id text-muted-foreground">
          <span className="font-medium">#{String(card.seq)}</span>
          <span className="text-faint-foreground">·</span>
          <ChannelChip channel={card.originChannel} />
          <span className="flex-1" />
          {waiting ? (
            <ToneChip tone="amber" icon="clock-countdown">
              {t('board.card.waiting')}
            </ToneChip>
          ) : null}
          {card.customer.vip ? <VipBadge /> : null}
        </div>

        <h3 className="m-0 line-clamp-2 text-card-title font-medium leading-card-title text-foreground">
          {card.subject ?? t('board.card.no_subject')}
        </h3>

        <div className="flex min-w-0 items-center gap-1.5 text-meta text-muted-foreground">
          <Avatar size="sm">
            <AvatarFallback displayName={card.customer.name} />
          </Avatar>
          <span className="min-w-0 truncate text-base font-medium leading-card-body text-foreground">
            {card.customer.name}
          </span>
          <span className="text-faint-foreground">·</span>
          <span className="min-w-0 truncate">{card.company.name}</span>
        </div>

        {showFooter ? (
          <footer className="flex items-center gap-2 font-mono text-card-id text-muted-foreground">
            {total > 0 ? (
              <>
                <ProgressBar statuses={statuses} />

                <span className="tabular-nums">
                  {done}/{total}
                </span>
              </>
            ) : null}
            {actor ? (
              <div className="min-w-0 ml-auto flex items-end">
                <RoleBadge kind={actor.kind} name={actor.name} />
              </div>
            ) : null}
          </footer>
        ) : null}
      </div>
    </Card>
  );
}
